import { inject, Injectable, Type } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import {
  BulkDeleteConfirmationComponent,
  BulkDeleteRow,
} from '../components/dialogs/bulk-delete-confirmation/bulk-delete-confirmation.component';
import { AccessRightDomain } from '../types/access-right.interface';
import { EntityDefinition } from '../types/entity-definition.interface';
import { EntityFormResult } from '../types/entity-form-result.interface';
import { GenericEntity } from '../types/generic-entity.interface';
import { FormRegistryService } from './form-registry.service';
import { ModalHelperService, ModalSize } from './modal-helper.service';

export interface EntityDialogOptions<T extends GenericEntity> {
  header: string;
  headerParams?: Record<string, unknown>;
  size?: ModalSize;
  width?: string;
  height?: string;
  maximizable?: boolean;
  entityDefinition: EntityDefinition<T>;
  mode: 'create' | 'edit' | 'bulk-edit' | 'bulk-delete';
  parentId?: number;
  additionalData?: Record<string, unknown>;
  onDelete?: (entity: T) => void;
}

export interface EntityDialogData<T extends GenericEntity> {
  entity?: T;
  entityDefinition: EntityDefinition<T>;
  parentId?: number;
  mode: 'create' | 'edit' | 'bulk-edit' | 'bulk-delete';
  additionalData?: Record<string, unknown>;
  onDelete?: (entity: T) => void;
}

@Injectable({
  providedIn: 'root',
})
// Wrapper pour le dialog service qui va aller chercher le formulaire dynamique
// pour l'entité puis l'afficher et retourner le résultat.
export class EntityDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly formRegistry = inject(FormRegistryService);
  private readonly translate = inject(TranslocoService);
  private readonly modalHelper = inject(ModalHelperService);

  openCreateDialog<T extends GenericEntity>(options: EntityDialogOptions<T>): Observable<EntityFormResult<T> | undefined> {
    const formComponent = this.getFormComponentForEntityType(options.entityDefinition.entityType);
    const header = this.translateHeader(options.header, options.headerParams);

    const data: EntityDialogData<T> = {
      entityDefinition: options.entityDefinition,
      mode: 'create',
      ...(options.parentId !== undefined && { parentId: options.parentId }),
      ...(options.additionalData !== undefined && { additionalData: options.additionalData }),
    };

    const ref = this.dialogService.open(formComponent, this.buildFormConfig(header, data, options));

    return ref.onClose as Observable<EntityFormResult<T> | undefined>;
  }

  openEditDialog<T extends GenericEntity>(entity: T, options: EntityDialogOptions<T>): Observable<EntityFormResult<T> | undefined> {
    const formComponent = this.getFormComponentForEntityType(options.entityDefinition.entityType);
    const header = this.translateHeader(options.header, options.headerParams);

    const data: EntityDialogData<T> = {
      entity,
      entityDefinition: options.entityDefinition,
      mode: options.mode,
      ...(options.parentId !== undefined && { parentId: options.parentId }),
      ...(options.additionalData !== undefined && { additionalData: options.additionalData }),
      ...(options.onDelete !== undefined && { onDelete: options.onDelete }),
    };

    const ref = this.dialogService.open(formComponent, this.buildFormConfig(header, data, options));

    return ref.onClose as Observable<EntityFormResult<T> | undefined>;
  }

  openBulkDeleteDialog<T extends GenericEntity>(options: EntityDialogOptions<T>): Observable<boolean | undefined> {
    const header = this.translateHeader(options.header, options.headerParams);
    const accessRights = (options.additionalData?.['accessRights'] ?? []) as AccessRightDomain[];
    const rows: BulkDeleteRow[] = accessRights.map(ar => ({
      label: `${ar.assetGroupName ?? ''} - ${ar.assetName ?? ''}`,
      detail: ar.roleName ?? '',
    }));

    const ref = this.dialogService.open(
      BulkDeleteConfirmationComponent,
      this.modalHelper.getSmallConfig({
        // Headless centered card chrome (no PrimeNG header bar) to match the single-delete confirm card.
        showHeader: false,
        data: {
          title: header,
          messageKey: 'entities.access.messages.bulkDeleteDescription',
          toDeleteKey: 'entities.access.messages.toDelete',
          rows,
        },
      }),
    );

    return ref.onClose as Observable<boolean | undefined>;
  }

  private buildFormConfig<T extends GenericEntity>(header: string, data: EntityDialogData<T>, options: EntityDialogOptions<T>) {
    return this.modalHelper.getConfig(options.size ?? 'form', {
      header,
      data,
      ...(options.width !== undefined && { width: options.width }),
      ...(options.height !== undefined && { height: options.height }),
      ...(options.maximizable !== undefined && { maximizable: options.maximizable }),
    });
  }

  private getFormComponentForEntityType(entityType: string): Type<unknown> {
    return this.formRegistry.getFormComponentForEntity(entityType);
  }

  private translateHeader(header: string, params?: Record<string, unknown>): string {
    if (header.startsWith('entities.') || header.startsWith('common.')) {
      return this.translate.translate(header, params);
    }
    return header;
  }
}
